import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Users, Edit2, Trash2, Mail, Phone, MapPin, ArrowLeft, Save, UserCheck, FileText, X as IconX, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api';
import { SideDrawer } from '../../components/SideDrawer';
import { CustomSelect } from '../../components/CustomSelect';
import { IconAction, RowActions } from '../../components/RowActions';

// Reusable Field Component
const Field = ({ label, icon: Icon, error, required, children }) => (
  <label className="block space-y-1 w-full">
    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label} {required && <span className="text-rose-500">*</span>}
    </span>
    <div className={`w-full [&_input]:w-full [&_input]:rounded-lg [&_input]:bg-gray-50 [&_input]:border ${error ? '[&_input]:border-rose-400 [&_input]:focus:border-rose-500' : '[&_input]:border-gray-100 [&_input]:focus:border-[var(--theme-primary)]'} [&_input]:focus:bg-white [&_input]:outline-none [&_input]:px-3 [&_input]:py-2 [&_input]:text-xs [&_input]:font-medium [&_input]:text-[#0d0d0d] [&_input]:transition-colors [&_textarea]:w-full [&_textarea]:rounded-lg [&_textarea]:bg-gray-50 [&_textarea]:border ${error ? '[&_textarea]:border-rose-400 [&_textarea]:focus:border-rose-500' : '[&_textarea]:border-gray-100 [&_textarea]:focus:border-[var(--theme-primary)]'} [&_textarea]:focus:bg-white [&_textarea]:outline-none [&_textarea]:px-3 [&_textarea]:py-2 [&_textarea]:text-xs [&_textarea]:font-medium [&_textarea]:text-[#0d0d0d] [&_textarea]:transition-colors [&_textarea]:resize-none`}>
      {children}
    </div>
    {error && <p className="text-[11px] font-medium text-rose-500 mt-1">{error}</p>}
  </label>
);

export const CustomersTab = ({ org, showNotification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCustomers, setTotalCustomers] = useState(0);

  // Instead of modal, we use viewState
  const [viewState, setViewState] = useState('list'); // 'list' | 'form'
  const [editItem, setEditItem] = useState(null);

  // Form state
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });
  const [errors, setErrors] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(`/customers?page=${page}&limit=${perPage}`, { headers: { 'x-org-id': org.id } });
      const data = await res.json();
      setTotalCustomers(parseInt(res.headers.get('X-Total-Count') || data?.length || 0, 10));
      setCustomers(data || []);
    } catch (err) {
      console.error(err);
      showNotification('Failed to fetch customers.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (org?.id) fetchData();
  }, [org?.id, page, perPage]);

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validations
    const newErrors = {};
    if (!form.name || form.name.trim() === '') {
      newErrors.name = 'Customer name is required';
    }
    
    const phoneRegex = /^\d{10}$/;
    if (!form.phone || !phoneRegex.test(form.phone.trim())) {
      newErrors.phone = 'Mobile number must be exactly 10 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return showNotification('Please fix the highlighted fields.', 'error');
    }
    setErrors({});

    try {
      const payload = { ...form };

      if (editItem) {
        await fetchWithAuth(`/customers/${editItem.id}`, {
          method: 'PUT',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        showNotification('Customer updated successfully.', 'success');
      } else {
        await fetchWithAuth('/customers', {
          method: 'POST',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        showNotification('Customer created successfully.', 'success');
      }
      setViewState('list');
      setEditItem(null);
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification('Error saving customer.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await fetchWithAuth(`/customers/${id}`, {
        method: 'DELETE',
        headers: { 'x-org-id': org.id }
      });
      showNotification('Customer deleted successfully.', 'success');
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification('Failed to delete customer.', 'error');
    }
  };

  const filteredCustomers = customers.filter(c => 
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (c.phone || '').includes(searchTerm)
  );

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(totalCustomers / perPage));
  const paginatedCustomers = filteredCustomers;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page on search
  };

  return (
    <div className="flex flex-col gap-6 flex-1 min-h-0 animate-fade-in pb-10">
      {/* Action Header Section */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 shadow-sm">
        <div className="relative flex-1 min-w-[180px] max-w-lg">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
            />
          </div>
          <button
            onClick={() => {
              setEditItem(null);
              setForm({ name: '', phone: '', email: '', address: '', notes: '' });
              setViewState('form');
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] transition-colors shadow-lg shrink-0"
            style={{ background: 'var(--theme-primary)', boxShadow: '0 12px 24px -12px var(--theme-primary)' }}
          >
            <Plus size={14} aria-hidden="true" /> New Customer
        </button>
      </section>

      <div className="flex-1 flex flex-col min-h-0">
        {/* Grid */}
        {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-2 border-[var(--theme-primary)] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="flex flex-col flex-1 min-h-0">
              <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden relative shadow-sm flex flex-col min-h-0 w-full flex-1">
                <div className="overflow-auto overscroll-none flex-1 custom-scrollbar min-h-0 table-scrollbar">
                <table className="w-full text-left border-collapse relative">
                  <thead className="bg-gray-50/50 sticky top-0 z-10 shadow-[0_1px_0_0_var(--theme-tertiary)]">
                    <tr>
                        <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[25%]">Customer Name</th>
                        <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[20%]">Contact Info</th>
                        <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[40%]">Address</th>
                        <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right w-[15%]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                    {filteredCustomers.length === 0 && !loading && (
                      <tr>
                        <td colSpan="4" className="px-5 py-16 text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                          {searchTerm ? 'No customers found for your search.' : 'No customers yet.'}
                        </td>
                      </tr>
                    )}
                    {paginatedCustomers.map(customer => (
                          <tr key={customer.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors group">
                            <td className="px-5 py-3 overflow-hidden">
                              <p className="text-[13px] font-extrabold text-[#0d0d0d] uppercase tracking-wide truncate" title={customer.name}>{customer.name}</p>
                            </td>
                            <td className="px-5 py-3 overflow-hidden">
                              <p className="text-[12px] font-semibold text-slate-700 truncate" title={customer.phone}>{customer.phone}</p>
                            </td>
                            <td className="px-5 py-3 overflow-hidden">
                              {customer.address ? (
                                <p className="text-[12px] font-medium text-slate-500 truncate" title={customer.address}>{customer.address}</p>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-5 py-3 text-right">
                              <RowActions>
                                <IconAction
                                  icon={Edit2}
                                  label={`Edit ${customer.name}`}
                                  title="Edit"
                                  onClick={() => {
                                    setEditItem(customer);
                                    setForm({
                                      name: customer.name || '',
                                      phone: customer.phone || '',
                                      email: customer.email || '',
                                      address: customer.address || '',
                                      notes: customer.notes || ''
                                    });
                                    setViewState('form');
                                  }}
                                />
                                <IconAction
                                  icon={Trash2}
                                  tone="danger"
                                  label={`Remove ${customer.name}`}
                                  title="Remove"
                                  onClick={() => handleDelete(customer.id)}
                                />
                              </RowActions>
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>

                {/* Pagination Footer */}
                {totalPages > 1 && (
                  <section className="px-5 py-3 flex items-center justify-between text-[11px] font-bold text-slate-500 border-t border-[var(--theme-tertiary)] bg-white shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="uppercase tracking-widest">Showing</span>
                      <CustomSelect
                        value={perPage}
                        onChange={(val) => {
                          setPerPage(Number(val));
                          setPage(1);
                        }}
                        options={[
                          { value: 10, label: '10' },
                          { value: 25, label: '25' },
                          { value: 50, label: '50' },
                          { value: 100, label: '100' },
                          { value: filteredCustomers.length > 0 ? filteredCustomers.length : 200, label: 'All' }
                        ]}
                        className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
                        menuUp={true}
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="uppercase tracking-widest">
                        PAGE <strong className="text-slate-800 mx-0.5">{page}</strong> OF <strong className="text-slate-800 ml-0.5">{totalPages}</strong>
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                          <ChevronLeft size={14} />
                        </button>
                        <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  </section>
                )}
              </section>
              </div>
            )}
          </div>
      {/* 2) FORM VIEW */}
      {viewState === 'form' && (
        <SideDrawer onClose={() => setViewState('list')} maxWidth="max-w-2xl">
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg text-white flex items-center justify-center" style={{ background: 'var(--theme-primary)' }}>
                <Users size={18} />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-[#0d0d0d]">{editItem ? 'Edit customer' : 'New customer'}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Manage customer details</p>
              </div>
            </div>
            <button type="button" onClick={() => setViewState('list')} aria-label="Close" className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50"><IconX size={16} /></button>
          </header>
          
          <div className="px-6 py-5 flex flex-col gap-4 flex-1 overflow-y-auto custom-scrollbar">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full Name" icon={UserCheck} error={errors.name} required>
                <input type="text" required value={form.name} onChange={e => {
                  setForm({...form, name: e.target.value});
                  if (errors.name) setErrors({...errors, name: undefined});
                }} placeholder="e.g. John Doe" />
              </Field>
              <Field label="Phone Number" icon={Phone} error={errors.phone} required>
                <input type="text" required value={form.phone} onChange={e => {
                  setForm({...form, phone: e.target.value});
                  if (errors.phone) setErrors({...errors, phone: undefined});
                }} placeholder="+1 (555) 000-0000" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Email Address" icon={Mail}>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@example.com" />
              </Field>
              <Field label="Address" icon={MapPin}>
                <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="123 Main St..." />
              </Field>
            </div>

            <Field label="Private Notes" icon={FileText}>
              <textarea rows="3" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="VIP customer, prefers morning appointments..." className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:border-[var(--theme-primary)] focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-colors resize-none"></textarea>
            </Field>
          </div>

          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0" style={{ background: 'var(--theme-secondary)' }}>
            <button type="button" onClick={() => setViewState('list')} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button onClick={handleSave} type="button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: 'var(--theme-primary)' }}>
              {editItem ? 'Save customer' : 'Create customer'}
            </button>
          </footer>
        </SideDrawer>
      )}
    </div>
  );
};
